import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { PagedResult, PaginationParams } from '../../core/models/api.models';
import {
  BacklogIssueDto,
  CreateSprintRequest,
  UpdateSprintRequest,
  SprintBoardDto,
  SprintDetailDto,
  SprintDto,
  SprintRetrospectiveDto,
  UpsertSprintRetrospectiveRequest
} from './scrum.models';

@Injectable({ providedIn: 'root' })
export class ScrumApiService {
  private readonly api = inject(ApiService);
  private readonly base = 'v1/scrum';

  getBacklog(projectId: string, params: PaginationParams = {}): Observable<PagedResult<BacklogIssueDto>> {
    return this.api.getPaged<BacklogIssueDto>(`${this.base}/projects/${projectId}/backlog`, params);
  }

  listSprints(params: PaginationParams & { projectId?: string; status?: number } = {}): Observable<PagedResult<SprintDto>> {
    return this.api.getPaged<SprintDto>(`${this.base}/sprints`, params);
  }

  getSprint(id: string): Observable<SprintDetailDto> {
    return this.api.get<SprintDetailDto>(`${this.base}/sprints/${id}`);
  }

  createSprint(request: CreateSprintRequest): Observable<SprintDto> {
    return this.api.post<SprintDto>(`${this.base}/sprints`, request);
  }

  updateSprint(id: string, request: UpdateSprintRequest): Observable<SprintDto> {
    return this.api.put<SprintDto>(`${this.base}/sprints/${id}`, request);
  }

  deleteSprint(id: string): Observable<unknown> {
    return this.api.delete(`${this.base}/sprints/${id}`);
  }

  addIssues(sprintId: string, issueIds: string[]): Observable<SprintDetailDto> {
    return this.api.post<SprintDetailDto>(`${this.base}/sprints/${sprintId}/issues`, { issueIds });
  }

  removeIssue(sprintId: string, issueId: string): Observable<unknown> {
    return this.api.delete(`${this.base}/sprints/${sprintId}/issues/${issueId}`);
  }

  getBoard(sprintId: string): Observable<SprintBoardDto> {
    return this.api.get<SprintBoardDto>(`${this.base}/sprints/${sprintId}/board`);
  }

  moveOnBoard(sprintId: string, issueId: string, status: number): Observable<BacklogIssueDto> {
    return this.api.post<BacklogIssueDto>(`${this.base}/sprints/${sprintId}/issues/${issueId}/move`, { status });
  }

  startSprint(sprintId: string): Observable<SprintDto> {
    return this.api.post<SprintDto>(`${this.base}/sprints/${sprintId}/start`, {});
  }

  completeSprint(
    sprintId: string,
    moveIncompleteToBacklog = true,
    moveIncompleteToSprintId?: string | null
  ): Observable<SprintDto> {
    return this.api.post<SprintDto>(`${this.base}/sprints/${sprintId}/complete`, {
      moveIncompleteToBacklog,
      moveIncompleteToSprintId: moveIncompleteToSprintId || null
    });
  }

  cancelSprint(sprintId: string): Observable<SprintDto> {
    return this.api.post<SprintDto>(`${this.base}/sprints/${sprintId}/cancel`, {});
  }

  getRetrospective(sprintId: string): Observable<SprintRetrospectiveDto> {
    return this.api.get<SprintRetrospectiveDto>(`${this.base}/sprints/${sprintId}/retrospective`);
  }

  upsertRetrospective(sprintId: string, request: UpsertSprintRetrospectiveRequest): Observable<SprintRetrospectiveDto> {
    return this.api.put<SprintRetrospectiveDto>(`${this.base}/sprints/${sprintId}/retrospective`, request);
  }
}
